
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook for real-time syncing of booking information for a user
 * 
 * @param options
 *  - userId: Only monitor records for the current user (required)
 *  - type: 'client' | 'practitioner' (monitoring role)
 *  - onBookingUpdate: (booking) => void  Callback for when a record is updated with latest data
 *  - onBookingInsert: (booking) => void  Callback for new record insertions (optional)
 *  - onBookingDelete: (booking) => void  Callback for record deletions (optional)
 */
type BookingRealtimeOptions = {
  userId: string | null | undefined;
  type: 'client' | 'practitioner';
  onBookingUpdate: (booking: any) => void;
  onBookingInsert?: (booking: any) => void;
  onBookingDelete?: (booking: any) => void;
};

export function useBookingRealtimeSync({ 
  userId, 
  type, 
  onBookingUpdate,
  onBookingInsert,
  onBookingDelete
}: BookingRealtimeOptions) {
  const channelRef = useRef<any>(null);
  const isSubscribed = useRef(false);

  useEffect(() => {
    if (!userId) return;
    
    // Prevent multiple subscriptions
    if (isSubscribed.current && channelRef.current) {
      console.log('Realtime subscription already active, skipping');
      return;
    }

    console.log(`Setting up realtime sync for ${type} with ID: ${userId}`);

    // Only monitor records related to the current client or practitioner
    const filter = type === 'client'
      ? `client_id=eq.${userId}`
      : `practitioner_id=eq.${userId}`;

    // Create a stable channel name to avoid recreating channels unnecessarily
    const channelName = `bookings-realtime-${userId}`;
    console.log(`Creating channel: ${channelName}`);

    try {
      // Clean up any existing channel first
      if (channelRef.current) {
        console.log(`Cleaning up existing channel: ${channelName}`);
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            // Updated row with latest data
            if (payload.new) {
              console.log('Booking update received:', payload.new);
              onBookingUpdate(payload.new);
            }
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            // New row inserted
            if (payload.new) {
              console.log('New booking received:', payload.new);
              if (onBookingInsert) {
                onBookingInsert(payload.new);
              } else {
                // If no separate insert handler is provided, use the update handler
                onBookingUpdate(payload.new);
              }
            }
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'bookings',
            filter,
          },
          payload => {
            // Deleted row
            if (payload.old) {
              console.log('Booking delete received:', payload.old);
              if (onBookingDelete) {
                onBookingDelete(payload.old);
              }
            }
          },
        )
        .subscribe((status) => {
          console.log(`Subscription status for ${channelName}:`, status);
          if (status === 'SUBSCRIBED') {
            isSubscribed.current = true;
          } else if (status === 'CLOSED') {
            isSubscribed.current = false;
          }
        });

      channelRef.current = channel;
      console.log('Realtime subscription established');

      return () => {
        console.log(`Cleaning up realtime channel: ${channelName}`);
        isSubscribed.current = false;
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error setting up realtime subscription:', error);
      isSubscribed.current = false;
    }
  }, [userId, type, onBookingUpdate, onBookingInsert, onBookingDelete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        console.log('Component unmounting - cleaning up realtime channel');
        isSubscribed.current = false;
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);
}
